import { GameCategory } from '../../enums/optic-odds.enum';
import { InPlay, MatchScores } from '../../interfaces/games';

/**
 * Best-of-N for a tennis match.
 *
 * Nothing in the codebase recorded this before. OpticOdds does not publish it:
 * measured 2026-08-11 against a live fixture, neither the fixture object (38 keys)
 * nor the results row carries any best_of / format / num_sets field. So it has to
 * be derived, and it is derived ONCE at match creation and persisted — never
 * recomputed in the settlement or pricing path, where a miss would be silent.
 *
 * The rule:
 *   WTA  -> always 3.
 *   ATP  -> 5 only in the MAIN DRAW of a grand slam; 3 everywhere else, including
 *           slam qualifying, which is best-of-3 for men.
 *
 * Doubles never reaches here: OpticOdds files it under separate leagues
 * ("ATP Doubles" / "WTA Doubles"), which map to no GameCategory of ours.
 */
export type TennisBestOf = 3 | 5;

/**
 * The categories best_of applies to. TENNIS is the meta-category spanning both
 * tours, so membership here says "this is tennis", not "this is men's tennis" —
 * the tour is decided from the fixture's own league string. Mirrors GOLF_CATEGORIES.
 */
export const TENNIS_CATEGORIES: ReadonlySet<GameCategory> = new Set([
	GameCategory.ATP,
	GameCategory.WTA,
	GameCategory.TENNIS,
]);

/**
 * The four grand slams, as OpticOdds spells them. Verified 2026-08-11 by paging
 * the full /tournaments?sport=tennis list — 8 pages, 430 unique names — and
 * matching on slam words.
 *
 * These are matched EXACTLY and deliberately. The tempting shortcut is structural:
 * slams look like "<Slam>, <City>, <Country>" while tour stops are "<City>, <Country>",
 * so "two or more commas" reads like a slam test. It is not — more than fifty doubles
 * events share that shape ("Montreal, Canada, Doubles"), and every one of them would
 * have been scored best-of-5. Add a name here; do not infer one.
 */
const SLAM_TOURNAMENT_NAMES: ReadonlySet<string> = new Set([
	'Australian Open, Melbourne, Australia',
	'French Open, Paris, France',
	'Wimbledon, London, Great Britain',
	'US Open, New York, USA',
]);

/**
 * Slam qualifying is best-of-3 for men. OpticOdds marks it in two places on the
 * fixture — tournament_stage ("1st Qualifying Round") and season_type
 * ("Cincinnati, USA, Qualifying") — so both are checked; either is sufficient.
 */
const isQualifying = (...markers: (string | null | undefined)[]): boolean =>
	markers.some((m) => !!m && /qualifying/i.test(m));

/**
 * True when the fixture belongs to the men's tour. Prefers the OpticOdds league
 * string because GameCategory.TENNIS is a META-category spanning both tours and
 * therefore cannot answer this on its own.
 */
const isMensTour = (oddsApiLeague?: string | null, category?: GameCategory | null): boolean => {
	if (oddsApiLeague) return oddsApiLeague.trim().toUpperCase() === 'ATP';
	return category === GameCategory.ATP;
};

export interface TennisBestOfInput {
	/** Raw OpticOdds league name, e.g. "ATP" / "WTA". Preferred over category. */
	oddsApiLeague?: string | null;
	/** Our GameCategory. Fallback only — TENNIS spans both tours and cannot decide. */
	category?: GameCategory | null;
	/** Raw OpticOdds tournament name, e.g. "US Open, New York, USA". */
	tournamentName?: string | null;
	/** Raw OpticOdds tournament_stage, e.g. "1st Qualifying Round". */
	tournamentStage?: string | null;
	/** Raw OpticOdds season_type, e.g. "Cincinnati, USA, Qualifying". */
	seasonType?: string | null;
}

/**
 * Returns 5 only for a men's grand-slam main-draw match; 3 otherwise.
 *
 * Defaults to 3 on every uncertainty — unknown league, missing tournament name,
 * an unrecognised slam spelling. Best-of-3 is the overwhelmingly common case
 * (all WTA, all ATP tour play, all slam qualifying), so an unknown that guessed
 * 5 would be wrong far more often than one that guesses 3.
 */
export const deriveTennisBestOf = ({
	oddsApiLeague,
	category,
	tournamentName,
	tournamentStage,
	seasonType,
}: TennisBestOfInput): TennisBestOf => {
	if (!isMensTour(oddsApiLeague, category)) return 3;
	if (!tournamentName || !SLAM_TOURNAMENT_NAMES.has(tournamentName.trim())) return 3;
	if (isQualifying(tournamentStage, seasonType)) return 3;

	return 5;
};

/** Sets one player must win to take the match. */
export const setsToWinMatch = (bestOf: TennisBestOf): number => (bestOf === 5 ? 3 : 2);

/** Never report a match as fully complete — the caller already skips completed games. */
const MAX_PROGRESS = 0.99;

/**
 * P(the stronger player wins any given game). THE ONLY FREE PARAMETER IN THE MODEL, and
 * it is pinned by measurement rather than chosen: 59 completed ATP/WTA matches
 * (2026-08-04..10) went to a deciding set 40.7% of the time (24 of 59), and 0.5798 is the
 * per-game probability that reproduces exactly that rate through the DP below. It implies
 * P(stronger wins a set) = 0.716 and E[total games] of 22.7 for Bo3 / 36.4 for Bo5.
 *
 * Everything else — set outcomes, match length, best-of-5 — is arithmetic from here.
 * That matters: the Bo5 numbers are DERIVED by the same recursion rather than
 * extrapolated from a Bo3-fitted constant, which is what the previous version did.
 */
const P_GAME_STRONGER = 0.5798;

const PG = P_GAME_STRONGER;

/**
 * Closed form for a set that has reached 6-6 or beyond with no two-game gap — the
 * "advantage" regime. Two more games either settle it or return to level, so the
 * expectation is a geometric series with an exact sum. Doing this analytically rather
 * than by recursion is not an optimisation: the recursive form has NO terminal state
 * on the diagonal and blows the stack on a long advantage set (found by the 20-19
 * bounds test, which is a perfectly realistic final-set score).
 */
const bothHold = 2 * PG * (1 - PG);
const TIED_GAMES = 2 / (1 - bothHold);
const TIED_WIN = (PG * PG) / (PG * PG + (1 - PG) * (1 - PG));

const setKey = (a: number, b: number) => a * 32 + b;
const setMemo = new Map<number, { games: number; win: number }>();

/**
 * From a game score, expected games left in THIS set and P(stronger takes it).
 * First to 6, win by 2; at 6-6 and beyond the advantage regime above takes over,
 * which also covers tiebreak-style finishes closely enough for a pace estimate.
 */
const setOutlook = (a: number, b: number): { games: number; win: number } => {
	if (a >= 6 && a - b >= 2) return { games: 0, win: 1 };
	if (b >= 6 && b - a >= 2) return { games: 0, win: 0 };

	// Advantage regime — bounded, and the only thing standing between this function
	// and unbounded recursion.
	if (a >= 6 && b >= 6) {
		if (a === b) return { games: TIED_GAMES, win: TIED_WIN };
		return a > b
			? { games: 1 + (1 - PG) * TIED_GAMES, win: PG + (1 - PG) * TIED_WIN }
			: { games: 1 + PG * TIED_GAMES, win: PG * TIED_WIN };
	}

	const key = setKey(a, b);
	const hit = setMemo.get(key);
	if (hit) return hit;

	const won = setOutlook(a + 1, b);
	const lost = setOutlook(a, b + 1);
	const out = {
		games: 1 + PG * won.games + (1 - PG) * lost.games,
		win: PG * won.win + (1 - PG) * lost.win,
	};
	setMemo.set(key, out);
	return out;
};

const matchMemo = new Map<string, number>();

/** Expected games remaining in the match from a full state. */
const gamesRemaining = (
	strongSets: number,
	weakSets: number,
	a: number,
	b: number,
	bestOf: TennisBestOf
): number => {
	const toWin = setsToWinMatch(bestOf);
	if (strongSets >= toWin || weakSets >= toWin) return 0;

	const key = `${strongSets}:${weakSets}:${a}:${b}:${bestOf}`;
	const hit = matchMemo.get(key);
	if (hit !== undefined) return hit;

	const cur = setOutlook(a, b);
	const out =
		cur.games +
		cur.win * gamesRemaining(strongSets + 1, weakSets, 0, 0, bestOf) +
		(1 - cur.win) * gamesRemaining(strongSets, weakSets + 1, 0, 0, bestOf);

	matchMemo.set(key, out);
	return out;
};

const toCount = (v: number | null | undefined): number =>
	typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;

/**
 * Fraction of the match completed, for tennis. THE FIRST STATE-BASED PROGRESS RESOLVER
 * IN THE CODEBASE — every other sport reads a clock, and tennis has none (OpticOdds
 * ships clock/time_min/time_sec all null). Golf, the other clockless sport, is a
 * `() => null` stub.
 *
 * MODEL: progress = gamesPlayed / (gamesPlayed + E[games remaining]), with the
 * expectation computed by exact recursion over game and set states. This targets the
 * ground truth directly — "fraction of the match elapsed" IS games-so-far over
 * total-games — rather than approximating it through set counts, which is what the
 * previous version did and why it drifted late.
 *
 * The stronger player is taken to be whoever leads on sets (ties broken by the current
 * set). That is information available at the time, not lookahead.
 *
 * MEASURED against the SHIPPED code (not a prototype) by replaying completed matches
 * game by game and comparing to ground-truth gamesPlayed/totalGames.
 *
 *   BEST-OF-3  69 WTA matches, 1,509 states — worst prediction bucket -0.040
 *   BEST-OF-5  68 men's slam main-draw matches (Wimbledon + French Open, grass + clay),
 *              2,558 states — worst prediction bucket -0.026
 *   BEST-OF-5  69 Australian Open main-draw matches (HARD COURT, the US Open surface),
 *              2,553 states — worst prediction bucket -0.026
 *
 * Bo5 is measured, and it is BETTER CALIBRATED THAN THE Bo3 that was already live, so
 * both formats are registered. Bo5 was gated off for one commit on the false premise
 * that it could not be measured before the US Open main draw; in fact it was merely
 * absent from the seven-day window originally sampled.
 *
 * SURFACE IS NOT A CONFOUND, AND THAT WAS CHECKED RATHER THAN ASSUMED. P_GAME_STRONGER
 * encodes a hold rate, so a surface that changes hold rates could in principle shift
 * calibration; hard court and grass/clay come out identical to three decimals. There is
 * therefore no outstanding "re-confirm during the US Open" item — it is already done,
 * on the same surface, before the event.
 *
 * WATCH OUT WHEN RE-MEASURING: OpticOdds STRIPS the whole `tournament` object off
 * fixtures older than roughly four months (measured 8/11: 0% populated for Jan and Mar
 * fixtures, 100% for May onward), while `season_type` carries the same string and is
 * populated in every window. Fixtures and results themselves are retained. This does
 * not affect production, which derives best-of from live fixtures that always carry
 * tournament.name — but a historical replay that keys on tournament.name will silently
 * label every aged slam as best-of-3 and quietly measure the wrong thing.
 *
 * BUCKET BY PREDICTION, NOT BY ACTUAL PROGRESS. Bucketing error by actual progress
 * conditions on the future — the states in the high-progress buckets are drawn from
 * matches that turned out to be long — so it manufactures a negative tail for ANY
 * model, including a perfect one. Both formats show that artifact (~-0.05 late) and
 * both are flat once bucketed by what the model actually said. An earlier version of
 * this comment treated the artifact as a defect and argued against registering.
 *
 * WHAT THE ERROR FEEDS. The bootstrap pricer is ITERATIVE: it writes newPrice back to
 * current_value and re-reads it as `base` next tick, so a systematic offset compounds
 * rather than settling. That is the property to watch in production. The counterfactual
 * is NOT a correct price — markets reaching this branch have no book quote and do not
 * move at all today — so the comparison is a small bias against no updates whatsoever.
 *
 * Returns null — do not bootstrap-price this match — whenever format or score state is
 * unknown, rather than guessing.
 */
export const tennisTimeProgressResolver = (
	inPlay: InPlay,
	scores?: MatchScores | null,
	bestOf?: number | null
): number | null => {
	if (bestOf !== 3 && bestOf !== 5) return null;

	if (!scores?.home || !scores?.away) return null;

	const homeSets = toCount(scores.home.total);
	const awaySets = toCount(scores.away.total);
	const setsDecided = homeSets + awaySets;
	if (setsDecided >= bestOf) return null; // finished or nonsensical — leave it alone
	if (Math.max(homeSets, awaySets) >= setsToWinMatch(bestOf)) return null; // already won

	// Current set from in_play.period ("2" on a live fixture; period_number carries the
	// same value but is absent from the InPlay type). Falls back to the decided count.
	const periodNumber = Number(inPlay?.period ?? NaN);
	const currentSet =
		Number.isFinite(periodNumber) && periodNumber > 0 ? periodNumber : setsDecided + 1;

	const gamesIn = (side: 'home' | 'away', set: number) =>
		toCount(scores[side]?.periods?.[`period_${set}`]);

	// Games played across every set so far, including the one in progress.
	let played = 0;
	for (let s = 1; s <= currentSet; s++) played += gamesIn('home', s) + gamesIn('away', s);

	const homeGames = gamesIn('home', currentSet);
	const awayGames = gamesIn('away', currentSet);

	const strongIsHome = homeSets !== awaySets ? homeSets > awaySets : homeGames >= awayGames;
	const remaining = gamesRemaining(
		strongIsHome ? homeSets : awaySets,
		strongIsHome ? awaySets : homeSets,
		strongIsHome ? homeGames : awayGames,
		strongIsHome ? awayGames : homeGames,
		bestOf
	);

	// A decided set with no games recorded is an inconsistent payload, not a match that
	// has produced zero games. Reporting 0% there would be a confident wrong answer.
	if (setsDecided > 0 && played === 0) return null;

	const denom = played + remaining;
	if (!Number.isFinite(denom) || denom <= 0) return null;

	const progress = played / denom;
	if (!Number.isFinite(progress)) return null;

	return Math.max(0, Math.min(progress, MAX_PROGRESS));
};
